import * as React from "react"
import { fabric } from "fabric"
import setupDevTools from "./devtools"
import vendingPlacehodler from "./../../assets/vending-placeholder-path.svg"
import uploadText from "./../../assets/click-here-to-customize-your-cart.png"
import shadowOverlayLayer from "./../../assets/shadow-highlight-overlay"

import {
  asyncLoadFabricImg,
  initializeCanvasBackground,
  getPathFromSvg,
  asyncSetOverlayImage,
} from "../../../../helpers/fabric"
import * as constants from "./constants"
import { setupEvents, initCustomControls } from "./helpers"
import useExtensions from "./useExtensions"

const isDev = process.env.NODE_ENV === "development"

const scaleWidth = constants.CANVAS_WIDTH
const scaleHeight = constants.CANVAS_HEIGHT

const useVendingCartDesigner = ({
  imgPath,
  logo,
  onUploadSectionClick,
  onRemoveLogo,
  activeExtensions,
}) => {
  const [isLoaded, setLoaded] = React.useState(false)
  const canvasInstance = React.useRef(null)
  const clippingPath = React.useRef(null)
  const box = React.useRef(null)

  useExtensions(canvasInstance.current, activeExtensions)

  const onDeleteUploadedImg = () => {
    canvasInstance.current.add(box.current)
    onRemoveLogo()
  }

  const initialize = async (node) => {
    initCustomControls({ onDelete: onDeleteUploadedImg })
    const canvas = new fabric.Canvas(node, constants.CANVAS_CONFIG)
    canvas.controlsAboveOverlay = true
    return canvas
  }

  const initFabricObjects = async (ctx) => {
    clippingPath.current = await getPathFromSvg(
      vendingPlacehodler,
      constants.PLACEHOLDER_PATH_OPTIONS
    )
    const img = await initializeCanvasBackground(ctx, imgPath)
    img.scaleToHeight(scaleHeight)
    img.scaleToWidth(scaleWidth)
    box.current = await asyncLoadFabricImg(uploadText)
    box.current.set(constants.UPLOAD_BOX_OPTIONS)
    await asyncSetOverlayImage(
      ctx,
      shadowOverlayLayer,
      constants.SHADOW_OVERLAY
    )
    ctx.add(box.current)
  }

  const canvasRef = React.useCallback(async (node) => {
    if (node === null) return null

    const canvas = await initialize(node)
    await initFabricObjects(canvas)

    if (isDev) {
      setupDevTools(canvas)
    }

    canvas.on("after:render", () => {
      setLoaded(true)
    })

    setupEvents(canvas, {
      onUploadSectionClick,
    })

    canvas.renderAll()
    canvasInstance.current = canvas
  }, [])

  React.useEffect(() => {
    if (canvasInstance.current && logo) {
      asyncLoadFabricImg(logo).then((logoObj) => {
        logoObj.set({
          ...constants.UPLOAD_IMG_OPTIONS,
          clipPath: clippingPath.current,
        })
        logoObj.scaleToWidth(200)
        canvasInstance.current.remove(box.current)
        canvasInstance.current.add(logoObj)
      })
    }
  }, [logo])

  React.useEffect(() => {
    const canvas = canvasInstance.current
    if (canvas) {
      initializeCanvasBackground(canvas, imgPath).then((img) => {
        img.scaleToHeight(scaleHeight)
        img.scaleToWidth(scaleWidth)
        canvas?.renderAll()
        setLoaded(true)
      })
    }
  }, [imgPath])

  return {
    isLoaded,
    canvasRef,
    ctx: canvasInstance.current,
  }
}

export default useVendingCartDesigner
